/**
 * MessageBubble Component
 * Renders an individual chat message with support for:
 * - Text (markdown-style formatting)
 * - HTML content (sanitized)
 * - Loading/streaming states
 * - Tool results display
 */

import React, { useState, useRef, useEffect } from 'react';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useAIContext } from './AIContext';
import type { ChatMessageDisplay } from '../../hooks/useAIChat';

interface MessageBubbleProps {
  message: ChatMessageDisplay;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const { isDark } = useThemeContext();
  const { editingMessageId, startEditingMessage, cancelEditing, editMessageAndContinue } = useAIContext();
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isLoading = message.isLoading;

  const isEditing = editingMessageId === message.id;
  const [editContent, setEditContent] = useState(message.content);
  const [showEditButton, setShowEditButton] = useState(false);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus and auto-resize when editing starts
  useEffect(() => {
    if (isEditing && editTextareaRef.current) {
      editTextareaRef.current.focus();
      editTextareaRef.current.style.height = 'auto';
      editTextareaRef.current.style.height = `${Math.min(editTextareaRef.current.scrollHeight, 120)}px`;
    }
  }, [isEditing]);

  // Auto-resize textarea
  useEffect(() => {
    if (editTextareaRef.current && isEditing) {
      editTextareaRef.current.style.height = 'auto';
      editTextareaRef.current.style.height = `${Math.min(editTextareaRef.current.scrollHeight, 120)}px`;
    }
  }, [editContent, isEditing]);

  const handleEditSubmit = () => {
    if (editContent.trim() && editContent.trim() !== message.content) {
      editMessageAndContinue(message.id, editContent.trim());
    } else {
      cancelEditing();
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSubmit();
    }
    if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  return (
    <div
      className={`flex w-full mb-3 group ${isUser ? 'justify-end' : 'justify-start'}`}
      onMouseEnter={() => isUser && setShowEditButton(true)}
      onMouseLeave={() => setShowEditButton(false)}
    >
      <div className="relative max-w-[85%]">
        <div
          className="rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm"
          style={{
            backgroundColor: isEditing
              ? (isDark ? 'var(--bg-grey-light)' : '#F0F0F0')
              : isUser
                ? 'var(--primary)'
                : (isDark ? 'var(--bg-grey-light)' : '#F0F0F0'),
            color: isUser && !isEditing ? '#FFFFFF' : 'var(--dark)',
            borderBottomRightRadius: isUser ? '4px' : '16px',
            borderBottomLeftRadius: isUser ? '16px' : '4px',
          }}
        >
          {/* Edit mode */}
          {isEditing ? (
            <div>
              <textarea
                ref={editTextareaRef}
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={handleEditKeyDown}
                rows={1}
                className="w-full resize-none rounded-lg px-3 py-2 text-sm outline-none border"
                style={{
                  backgroundColor: isDark ? 'var(--light)' : '#FFFFFF',
                  color: 'var(--dark)',
                  borderColor: 'var(--primary)',
                  minHeight: '36px',
                  maxHeight: '120px',
                }}
                autoFocus
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleEditSubmit}
                  disabled={!editContent.trim()}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                  style={{
                    backgroundColor: 'var(--primary)',
                    color: '#FFFFFF',
                  }}
                >
                  Save & Send
                </button>
                <button
                  onClick={cancelEditing}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{
                    backgroundColor: 'var(--grey-light)',
                    color: 'var(--dark)',
                  }}
                >
                  Cancel
                </button>
              </div>
              <div className="text-[10px] mt-1 opacity-50" style={{ color: 'var(--grey-dark)' }}>
                Enter to save · Shift+Enter for new line · Esc to cancel
              </div>
            </div>
          ) : (
            <>
              {isLoading && (
                <div className="flex items-center gap-2" style={{ color: 'var(--grey-dark)' }}>
                  <span className="flex items-end gap-1 h-3" aria-hidden>
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: 'var(--primary)', animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: 'var(--primary)', animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: 'var(--primary)', animationDelay: '240ms' }} />
                  </span>
                  <span className="text-xs">{message.status || 'Thinking'}</span>
                </div>
              )}

              {message.isStreaming && message.status && !isLoading && (
                <div className="text-[11px] mb-1 opacity-70" style={{ color: 'var(--grey-dark)' }}>
                  {message.status}
                </div>
              )}

              {message.content && (
                <div className="whitespace-pre-wrap break-words">
                  {renderContent(message.content)}
                </div>
              )}

              {(!isLoading || !!message.content) && (
              <div
                className={`text-[10px] mt-1 ${isUser ? 'text-white/70 text-right' : 'text-gray-400'}`}
              >
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              )}
            </>
          )}
        </div>

        {/* Edit button (only shown for user messages) */}
        {isUser && !isEditing && showEditButton && !isLoading && (
          <button
            onClick={() => {
              setEditContent(message.content);
              startEditingMessage(message.id);
            }}
            className="absolute -left-8 top-2 p-1 rounded-md transition-all opacity-60 hover:opacity-100"
            style={{ color: 'var(--grey-dark)' }}
            title="Edit message"
            aria-label="Edit message"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Render message content with basic formatting
 */
function renderContent(content: string): React.ReactNode {
  if (!content) return null;
  content = content.replace(/\s*[—–―]+\s*/g, ', ');

  // Split by code blocks
  const parts = content.split(/(```[\s\S]*?```)/g);

  return parts.map((part, idx) => {
    // Code block
    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.slice(3, -3);
      const firstLineEnd = code.indexOf('\n');
      const lang = firstLineEnd > 0 ? code.slice(0, firstLineEnd).trim() : '';
      const codeContent = firstLineEnd > 0 ? code.slice(firstLineEnd + 1) : code;

      return (
        <pre
          key={idx}
          className="my-2 p-3 rounded-lg overflow-x-auto text-xs leading-relaxed"
          style={{
            backgroundColor: 'var(--dark-b)',
            color: 'var(--grey-light)',
          }}
        >
          {lang && <div className="text-[10px] uppercase tracking-wide mb-1 opacity-60">{lang}</div>}
          <code>{codeContent}</code>
        </pre>
      );
    }

    // Check for pipe tables - split text into table and non-table segments
    if (part.includes('|') && part.includes('---')) {
      return renderTextWithTables(part);
    }

    // Regular text with basic formatting
    return (
      <span key={idx}>
        {renderInlineFormatting(part)}
      </span>
    );
  });
}

/**
 * Render text that may contain pipe tables, preserving inline formatting for non-table parts
 */
function renderTextWithTables(text: string): React.ReactNode {
  // Split by table blocks (lines containing | and --- separator)
  const lines = text.split('\n');
  const segments: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    // Look for a table: at least 2 consecutive pipe-lines, one being a separator
    if (lines[i].includes('|') && (i + 2) < lines.length) {
      const tableLines: string[] = [];
      let j = i;

      // Collect consecutive pipe lines
      while (j < lines.length && lines[j].includes('|')) {
        tableLines.push(lines[j]);
        j++;
      }

      if (tableLines.length >= 2) {
        // Check if any line is a separator (contains ---)
        const hasSeparator = tableLines.some(l => l.includes('---'));
        if (hasSeparator) {
          const tableBlock = tableLines.join('\n');
          const table = renderPipeTable(tableBlock);
          if (table) {
            segments.push(<span key={`t${i}`}>{table}</span>);
            i = j;
            continue;
          }
        }
      }
    }

    // Not a table, render as inline formatted text with newline
    segments.push(
      <span key={`l${i}`}>
        {renderInlineFormatting(lines[i])}
        {i < lines.length - 1 && <br />}
      </span>
    );
    i++;
  }

  return segments;
}

/**
 * Render inline formatting: bold, italic, line breaks
 */
function renderInlineFormatting(text: string): React.ReactNode {
  if (!text) return null;

  // Process bold (**text**) - allow any characters except ** inside
  const boldParts = text.split(/(\*\*[\s\S]*?\*\*)/g);
  return boldParts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }

    // Process italic (*text*) - single line only
    const italicParts = part.split(/(\*[^*\n]+\*)/g);
    return italicParts.map((subPart, subIdx) => {
      if (subPart.startsWith('*') && subPart.endsWith('*') && !subPart.startsWith('**')) {
        return <em key={`${idx}-${subIdx}`}>{subPart.slice(1, -1)}</em>;
      }

      // Handle line breaks
      return subPart.split('\n').map((line, lineIdx, arr) => (
        <React.Fragment key={`${idx}-${subIdx}-${lineIdx}`}>
          {line}
          {lineIdx < arr.length - 1 && <br />}
        </React.Fragment>
      ));
    });
  });
}

/**
 * Render a pipe table (markdown-style) as an HTML table
 */
function renderPipeTable(text: string): React.ReactNode {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length < 2) return null;

  // Check if it looks like a table (has pipes on at least 2 lines)
  const pipeLines = lines.filter(l => l.includes('|'));
  if (pipeLines.length < 2) return null;

  // Detect separator line (| --- | --- |)
  const sepIndex = lines.findIndex(l => /^\|?[\s-:]+\|/.test(l.trim()));
  const headerLines = sepIndex >= 0 ? lines.slice(0, sepIndex) : [lines[0]];
  const bodyLines = sepIndex >= 0 ? lines.slice(sepIndex + 1) : lines.slice(1);

  if (headerLines.length === 0 || bodyLines.length === 0) return null;

  const parseRow = (row: string): string[] =>
    row.split('|').map(c => c.trim()).filter(c => c !== '');

  const headers = parseRow(headerLines[0]);
  if (headers.length === 0) return null;

  const rows = bodyLines.map(parseRow).filter(r => r.length > 0);
  if (rows.length === 0) return null;

  return (
    <div className="my-2 overflow-x-auto">
      <table
        className="w-full text-xs border-collapse"
        style={{ border: '1px solid var(--grey-light)' }}
      >
        <thead>
          <tr style={{ backgroundColor: 'var(--bg-grey-light)' }}>
            {headers.map((h, i) => (
              <th
                key={i}
                className="px-3 py-2 text-left font-semibold"
                style={{
                  borderBottom: '2px solid var(--grey-light)',
                  borderRight: i < headers.length - 1 ? '1px solid var(--grey-light)' : 'none',
                  color: 'var(--grey-dark)',
                }}
              >
                {renderInlineFormatting(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              style={{
                backgroundColor: ri % 2 === 0 ? 'transparent' : 'var(--bg-grey-light)',
              }}
            >
              {headers.map((_, ci) => (
                <td
                  key={ci}
                  className="px-3 py-1.5"
                  style={{
                    borderBottom: '1px solid var(--grey-light)',
                    borderRight: ci < headers.length - 1 ? '1px solid var(--grey-light)' : 'none',
                  }}
                >
                  {renderInlineFormatting(row[ci] || '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default MessageBubble;
